import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress
} from '@mui/material';
import { HelpOutline as HelpIcon } from '@mui/icons-material';
import SearchBar from './SearchBar';
import { useDashboardContext } from './DashboardContext';
import ChatComponent from './Components/ChatComponent';
import rxaiIcon from './assets/rxaiIcon.png';
import StartNewChatButton from './Components/StartNewChatButton';

function GPDashboardChat() {
  const {
    conversations,
    setConversation,
    chatIds,
    setChatId,
  } = useDashboardContext();

  const conversation = conversations.main_with_activity || [];
  const currentChatId = chatIds.main_with_activity;

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const chatWindowRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  const loadingMessages = [
    'Analyzing your question...',
    'Gathering relevant information...',
    'Verifying details...'
  ];

  const defaultQuestions = [
    'Show me how many packs of Simalvia were prescribed in the last 12 months?',
    'Show me a breakdown of my activity to prescribers in the last 12 months?',
  ];

  const [currentLoadingMessage, setCurrentLoadingMessage] = useState(0);

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setCurrentLoadingMessage((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [loading, loadingMessages.length]);

  useEffect(() => {
    scrollToBottom();
  }, [conversations.main_with_activity]);

  useEffect(() => {
    if (!conversation.length || !currentChatId) {
      startNewChat();
    }
  }, []);  

  const scrollToBottom = () => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  };

  const startNewChat = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/start_new_chat_gprx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json();
      setChatId('main_with_activity', data.thread_id);
      setConversation('main_with_activity', [{
        role: 'assistant',
        content: 'Welcome to RxAI! Ask a question to uncover valuable insights into pharmaceuticals, research, and data.'
      }]);
    } catch (error) {
      console.error('Error starting new chat:', error);
      setConversation('main_with_activity', [{ role: 'assistant', content: 'Failed to start a new chat. Please try again later.' }]);
    }
  };

  const handleSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    const userMessage = { role: 'user', content: searchQuery, timestamp: Date.now() };

    let updated = [];
    if (conversation.length === 1 && conversation[0]?.role === 'assistant') {
      updated = [userMessage];
    } else {
      updated = [...conversation, userMessage];
    }
    setConversation('main_with_activity', updated);

    try {
      const res = await fetch(`${apiUrl}/api/ask_gprx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question: searchQuery }),
        credentials: 'include',
      });
      const data = await res.json();
      setFollowUpQuestions(data.follow_up ? data.follow_up.split('\n').filter(Boolean) : []);
      if (data.answer.headers && data.answer.rows) {
        const tableMessage = {
          role: 'assistant',
          content: {
            type: 'table',
            headers: data.answer.headers,
            rows: data.answer.rows,
          },
          sql_query: data.sql_query,
          question: data.question,
          canDownload: data.can_download,
          timestamp: Date.now()
        };
        setConversation('main_with_activity', [...updated, tableMessage]);
      } else {
        const assistantMessageContent = `**RxAI:**\n${data.answer}`;
        const assistantMessage = {
          role: 'assistant',
          content: assistantMessageContent,
          sql_query: data.sql_query,
          question: data.question,
          canDownload: data.can_download,
          timestamp: Date.now()
        };
        setConversation('main_with_activity', [...updated, assistantMessage]);
      }

      if (data.graph) {
        const graphMessageContent = `**RxAI:**\n<h3>Graph Result</h3><img src="data:image/png;base64,${data.graph_image}" alt="Graph" style="max-width:100%;"/>`;
        const graphMessage = {
          role: 'assistant',
          content: graphMessageContent,
          timestamp: Date.now()
        };
        setConversation('main_with_activity', [...updated, graphMessage]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      const errorMessage = { role: 'assistant', content: `**RxAI:**\nSorry, something went wrong. Please try again later. 🚫 ${error}`, timestamp: Date.now() };
      setConversation('main_with_activity', [...updated, errorMessage]);
    } finally {
      setLoading(false);
      setQuery('');
    }
  };

  const handleNewChat = () => {
    setConversation('main_with_activity', []);
    setFollowUpQuestions([]);
    startNewChat();
  };

  // Style and layout improvements
  // Subtle fade-in animation for messages
  const fadeInStyle = {
    '@keyframes fadeIn': {
      from: { opacity: 0, transform: 'translateY(5px)' },
      to: { opacity: 1, transform: 'translateY(0)' }
    },
    animation: 'fadeIn 0.5s ease-in-out',
  };

  const backgroundGradient = '#FFFFFF';
  const loadingMessageBubbleBg = '#2EA1B6';
  const chatContainerBg = '#FFFFFF'; 

  return (
    <Box sx={{ padding: 3, background: backgroundGradient, minHeight: '100vh' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3, color: 'text.primary',letterSpacing: '0.5px', }}>
        GP Rx & Activity AI Dashboard
      </Typography>
      <Grid container spacing={3}>
        {/* Chat Section */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Box
                ref={chatWindowRef}
                sx={{
                  height: '60vh',
                  overflowY: 'auto',
                  padding: 2,
                  backgroundColor: chatContainerBg,
                  borderRadius: 1,
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* If only one assistant message and it's the initial one, show a centered welcome message with illustration */}
                {conversation.length === 1 && conversation[0].role === 'assistant' && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center',
                      padding: 4,
                    }}
                  >
                    <img src={rxaiIcon} style={{ width: 50, height: 50 }} />
                    <Typography variant="h5" gutterBottom>
                      Welcome to GP Rx & Activity AI!
                    </Typography>
                    <Typography variant="body1" sx={{ maxWidth: 400 }}>
                      Ask a question to uncover valuable insights into prescriptions, market share, and key data.
                    </Typography>
                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: 'center'
                      }}
                    >
                      {defaultQuestions.map((question, idx) => (
                        <Button
                          key={idx}
                          variant="outlined"
                          color="secondary"
                          onClick={() => handleSearch(question)}
                          sx={{
                            textTransform: 'none',
                            fontFamily: 'Roboto, sans-serif',
                            width: '100%',
                            maxWidth: 500,
                          }}
                        >
                          {question}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* If more than the initial message, show conversation */}
                {!(conversation.length === 1 && conversation[0].role === 'assistant') && (
                  <>
                    {conversation.map((msg, index) => {
                      return (
                        <ChatComponent index={index} message={msg}/>
                      );
                    })}
                    {loading && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          mb: 1,
                          ...fadeInStyle
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '80%',
                            padding: 1.5,
                            borderRadius: 2,
                            backgroundColor: loadingMessageBubbleBg,
                            color: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '1rem',
                            gap: 1
                          }}
                        >
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          <Typography variant="body2" sx={{ overflowWrap: 'break-word' }}>
                            {loadingMessages[currentLoadingMessage]}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* Follow-Up Questions */}
              {followUpQuestions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1">Follow-Up Questions:</Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {followUpQuestions.map((question, idx) => (
                      <Button
                        key={idx}
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleSearch(question)}
                        startIcon={<HelpIcon />}
                        sx={{
                          textTransform: 'none',
                          fontFamily: 'Roboto, sans-serif',
                        }}
                      >
                        {question}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Search Bar and New Chat Button */}
              <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {/* SearchBar is cleared after a question is asked, so the question doesn't remain in input */}
                <SearchBar
                  query={query}
                  setQuery={setQuery}
                  handleSearch={() => handleSearch(query)}
                  loading={loading}
                />
                <StartNewChatButton handleNewChat={handleNewChat}/>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default GPDashboardChat;